import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { EnvelopeIcon, LockClosedIcon, BeakerIcon, XMarkIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Button, Input } from '@/components/ui';
import { authApi, getErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import type { LoginInput } from '@/types';

const DEMO_ACCOUNTS = [
  {
    role: 'Admin',
    email: 'admin@hotel101.com',
    password: 'Admin@123',
    description: 'Full access to manage users, clients, and payment links',
  },
  {
    role: 'Staff',
    email: 'staff@hotel101.com',
    password: 'Staff@123',
    description: 'Can create and manage payment links',
  },
  {
    role: 'Client',
    email: 'client@example.com',
    password: 'Client@123',
    description: 'View-only access to their payment links',
  },
];

export function LoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const [isLoading, setIsLoading] = useState(false);
  const [isDemoOpen, setIsDemoOpen] = useState(true);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginInput>();

  const fillDemoCredentials = (email: string, password: string) => {
    setValue('email', email);
    setValue('password', password);
    toast.success('Demo credentials filled!');
  };

  const onSubmit = async (data: LoginInput) => {
    setIsLoading(true);
    try {
      const response = await authApi.login(data.email, data.password);
      const { user, tokens } = response.data.data;

      login(user, tokens.accessToken, tokens.refreshToken);
      toast.success('Welcome back!');

      // Redirect based on role
      if (user.role === 'CLIENT') {
        navigate('/client');
      } else {
        navigate('/admin');
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Input
          label="Email address"
          type="email"
          autoComplete="email"
          required
          leftIcon={<EnvelopeIcon className="w-5 h-5" />}
          error={errors.email?.message}
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Invalid email address',
            },
          })}
        />

        <Input
          label="Password"
          type="password"
          autoComplete="current-password"
          required
          leftIcon={<LockClosedIcon className="w-5 h-5" />}
          error={errors.password?.message}
          {...register('password', {
            required: 'Password is required',
            minLength: {
              value: 6,
              message: 'Password must be at least 6 characters',
            },
          })}
        />

        <div className="flex items-center justify-between">
          <label className="flex items-center">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="ml-2 text-sm text-gray-600">Remember me</span>
          </label>

          <Link
            to="/forgot-password"
            className="text-sm font-medium text-primary hover:text-primary-800"
          >
            Forgot password?
          </Link>
        </div>

        <Button type="submit" className="w-full" size="lg" isLoading={isLoading}>
          Sign in
        </Button>

        <div className="text-center text-sm text-gray-500">
          Don't have an account?{' '}
          <Link to="/register" className="font-medium text-primary hover:text-primary-800">
            Contact administrator
          </Link>
        </div>
      </form>

      {/* Demo Credentials Slide-out Panel */}
      <div
        className={`fixed top-1/2 -translate-y-1/2 right-0 z-50 transition-transform duration-300 ease-in-out ${
          isDemoOpen ? 'translate-x-0' : 'translate-x-[calc(100%-2.5rem)]'
        }`}
      >
        {/* Toggle Button */}
        <button
          onClick={() => setIsDemoOpen(!isDemoOpen)}
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full bg-amber-500 hover:bg-amber-600 text-white p-2 rounded-l-lg shadow-lg transition-colors"
          title={isDemoOpen ? 'Hide demo credentials' : 'Show demo credentials'}
        >
          {isDemoOpen ? (
            <ChevronRightIcon className="w-5 h-5" />
          ) : (
            <BeakerIcon className="w-5 h-5" />
          )}
        </button>

        {/* Panel Content */}
        <div className="bg-white border border-gray-200 rounded-l-xl shadow-2xl w-80 max-h-[80vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 rounded-tl-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BeakerIcon className="w-5 h-5 text-amber-600" />
                <h3 className="font-semibold text-amber-800">Demo Mode</h3>
              </div>
              <button
                onClick={() => setIsDemoOpen(false)}
                className="text-amber-600 hover:text-amber-800 p-1"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-amber-700 mt-1">
              For testing purposes only. Click to auto-fill credentials.
            </p>
          </div>

          {/* Demo Accounts */}
          <div className="p-3 space-y-2">
            {DEMO_ACCOUNTS.map((account) => (
              <button
                key={account.email}
                type="button"
                onClick={() => fillDemoCredentials(account.email, account.password)}
                className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-primary hover:bg-primary-50 transition-all group"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-gray-900 group-hover:text-primary">
                    {account.role}
                  </span>
                  <span className="text-xs px-2 py-0.5 bg-gray-100 group-hover:bg-primary-100 rounded-full text-gray-600 group-hover:text-primary-700">
                    Click to use
                  </span>
                </div>
                <div className="text-xs text-gray-500 space-y-0.5">
                  <div className="font-mono">{account.email}</div>
                  <div className="font-mono">Password: {account.password}</div>
                </div>
                <p className="text-xs text-gray-400 mt-1.5">{account.description}</p>
              </button>
            ))}
          </div>

          {/* Footer Warning */}
          <div className="bg-red-50 border-t border-red-100 px-4 py-2 rounded-bl-xl">
            <p className="text-xs text-red-600 text-center">
              Do not use demo credentials in production
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
